import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getMetadataBundle } from '../metadata/languageMeta.js';
import { unslugifyName, normalizeName } from '../utils/slugHelpers.js';
import { t } from '../i18n/translationStrings.js'; // <-- ADDED

const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL;

const truncateText = (text, max = 50) => (text.length > max ? text.slice(0, max) + '…' : text);

const scoreAuthorMatch = (authorBooks, candidateBooks, authorCategories, authorLanguage) => {
  let score = 0;

  candidateBooks.forEach(candidate => {
    // Category overlap
    const sharedCats = (candidate.categories || []).filter(cat => authorCategories.has(cat.toLowerCase()));
    const uniqueCats = new Set([...candidate.categories || []].map(c => c.toLowerCase()));
    score += sharedCats.length / (uniqueCats.size + 1);

    // Language match
    if (authorLanguage && candidate.language === authorLanguage) {
      score += 0.5; // Increased weighting
    }

    // Title keyword match
    const titleMatch = /\b(essays?|works|poems?|letters?|collected|thoughts?)\b/;
    if (
      authorBooks.some(book => titleMatch.test(book.title?.toLowerCase())) &&
      titleMatch.test(candidate.title?.toLowerCase())
    ) {
      score += 0.3;
    }

    // Publication year proximity
    const candidateYear = parseInt(candidate.publicationDate);
    authorBooks.forEach(book => {
      const year = parseInt(book.publicationDate);
      if (!isNaN(candidateYear) && !isNaN(year) && Math.abs(candidateYear - year) <= 20) {
        score += 0.2;
      }
    });
  });

  return score / candidateBooks.length; // normalize
};

const AuthorPage = () => {
  const { authorSlug } = useParams();
  const location = useLocation();
  const decodedAuthor = unslugifyName(decodeURIComponent(authorSlug));
  const [booksByAuthor, setBooksByAuthor] = useState([]);
  const [relatedAuthors, setRelatedAuthors] = useState([]);
  const [authorBio, setAuthorBio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allMetadata, setAllMetadata] = useState([]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${S3_BUCKET_URL}/HermesTest/metadata.json`);
        const metadata = await response.json();

        setAllMetadata(metadata); // ✅ Save for reuse

        const normalizeAuthor = normalizeName(decodedAuthor);
        const books = metadata
          .filter(book => normalizeName(book.author) === normalizeAuthor);

        if (books.length) {
          books.sort((a, b) => a.title.localeCompare(b.title));
        }
        setBooksByAuthor(books);

        if (books.length > 0 && books[0].author_biography) {
          setAuthorBio(books[0].author_biography);
        }

        setLoading(false);
      } catch (err) {
        console.error("Metadata fetch failed:", err);
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [authorSlug]);

  useEffect(() => {
    if (!booksByAuthor.length || !allMetadata.length) return;

    const timer = setTimeout(() => {
      const excludedAuthors = ['unknown', 'various', 'multiple authors', 'anonymous'].map(normalizeName);
      const currentAuthorName = normalizeName(decodedAuthor);

      const authorCategories = new Set(
        booksByAuthor.flatMap(b => (b.categories || []).map(c => c.toLowerCase()))
      );
      const currentLanguage = booksByAuthor[0]?.language;

      const candidateAuthors = {};

      allMetadata.forEach(book => {
        const name = book.author?.trim();
        if (!name || excludedAuthors.includes(normalizeName(name)) || normalizeName(name) === currentAuthorName) return;

        if (!candidateAuthors[name]) {
          candidateAuthors[name] = [];
        }
        candidateAuthors[name].push(book);
      });

      const scored = Object.entries(candidateAuthors)
        .map(([author, books]) => ({
          author,
          score: scoreAuthorMatch(booksByAuthor, books, authorCategories, currentLanguage)
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 12)
        .map(entry => entry.author)
        .filter(name => name.length > 2 && !/^\s*$/.test(name));

      setRelatedAuthors(scored);
    }, 100); // delay in milliseconds

    return () => clearTimeout(timer);
  }, [booksByAuthor, allMetadata]);

  const capitalizedAuthor = booksByAuthor[0]?.author || authorSlug.replace(/-/g, ' ');

  const metadata = getMetadataBundle('author', booksByAuthor[0]?.language || 'en', {
    author: capitalizedAuthor
  }, {
    url: `https://www.hermesreads.com${location.pathname}`,
    image: '/images/PlaceholderJPG.webp'
  });

  return (
    <main className="author-page">
      <Helmet>
        <html lang={metadata.htmlLang} />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />

        <meta property="og:title" content={metadata.og.title} />
        <meta property="og:description" content={metadata.og.description} />
        <meta property="og:image" content={metadata.og.image} />
        <meta property="og:type" content={metadata.og.type} />
        <meta property="og:url" content={metadata.og.url} />

        <meta name="twitter:card" content={metadata.twitter.card} />
        <meta name="twitter:title" content={metadata.twitter.title} />
        <meta name="twitter:description" content={metadata.twitter.description} />
        <meta name="twitter:image" content={metadata.twitter.image} />

        <link rel="canonical" href={metadata.og.url} />
        <script type="application/ld+json">{JSON.stringify(metadata.structuredData)}</script>
      </Helmet>

      {loading ? (
        <div className="loading-message">
          <h1>{t("loadingAuthorData")}</h1>
        </div>
      ) : (
        <div className="author-container">
          <h1>{capitalizedAuthor}</h1>

          {authorBio && (
            <p className="author-biography">{authorBio}</p>
          )}

          {booksByAuthor.length > 0 ? (
            <>
              <h2 className="author-section-title">
                {booksByAuthor.length} {t("booksBy")} {capitalizedAuthor}
              </h2>
              <div className="book-grid">
                {booksByAuthor.map(book => (
                  <Link to={`/book/${book.titleSlug}`} key={book.id || book.title}>
                    <div className="author-book-card">
                      <h4>{truncateText(book.title)}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--text-color)' }}>
              {t("noBooksFound")} {capitalizedAuthor}.
            </p>
          )}

          {relatedAuthors.length > 0 && (
            <>
              <h2 className="author-section-title">{t("relatedAuthors")}</h2>
              <ul>
                {relatedAuthors.map(ra => (
                  <li key={ra}>
                    <Link to={`/author/${encodeURIComponent(ra.replace(/ /g, '-'))}`}>
                      {ra}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </main>
  );
};

export default AuthorPage;